<template>
  <div class="area-selection">
    <div class="area-selection__form">
      <q-card-section class="area-selection__header flex q-gutter-md">
        <slot name="header" />
      </q-card-section>
      <q-card-section class="area-selection__content">
        <div class="area-selection__content-left">
          <div class="form-group">
            <q-input
              v-model="area.name"
              class="form-group__control"
              outlined
              :rules="[requiredRule]"
              lazy-rules="ondemand"
              label="Имя"
              @change="emitChange"
            />
          </div>

          <div class="form-group">
            <q-input
              v-model="area.description"
              class="form-group__control"
              outlined
              lazy-rules="ondemand"
              label="Описание"
              @change="emitChange"
            />
          </div>

          <div class="form-group q-gutter-md">
            <div class="form-group__control text-right">
              <q-btn
                v-if="!maxShapes || shapes.length < maxShapes"
                color="primary"
                icon="add"
                label="Добавить фигуру"
                @click="addMainShape"
              />
            </div>
          </div>

          <div class="figures-list">
            <div class="figures-list__scroll-area">
              <div
                v-for="(shape, shapeIndex) in shapes"
                :key="shapeIndex"
                class="polygon"
              >
                <div class="form-group form-group--horizontal q-gutter-md justify-between">
                  <div class="form-group__label text-h5">
                    Фигура
                  </div>
                  <div class="form-group__control polygon__controls">
                    <q-btn
                      v-if="!shape.polygon.editor.state.get('drawing')"
                      color="primary"
                      icon="edit"
                      @click="startDrawing(shape.polygon)"
                    />
                    <q-btn
                      v-if="shape.polygon.editor.state.get('drawing')"
                      color="primary"
                      icon="done"
                      @click="stopDrawing(shape.polygon)"
                    />
                    <q-btn
                      v-if="shapes.length > 1"
                      color="primary"
                      icon="delete"
                      @click="removeShape(shape)"
                    />
                  </div>
                </div>

                <q-separator class="q-mb-lg" />

                <div class="form-group q-gutter-md">
                  <div class="form-group__control text-right">
                    <q-btn
                      v-if="shape.contours.length < maxContours"
                      color="primary"
                      icon="add"
                      label="Добавить контур"
                      @click="addContour(shape.polygon)"
                    />
                  </div>
                </div>

                <div
                  v-for="(contour, contourIndex) in shape.contours"
                  :key="contourIndex"
                  class="figure form-group"
                  @click="selectContour(shape.polygon, contourIndex)"
                >
                  <q-field
                    :value="contour.getLength()"
                    outlined
                    label="Контур"
                    class="form-group__control"
                    :rules="[requiredRule, numberRangeRule]"
                    lazy-rules="ondemand"
                    :class="[{
                      'q-field--focused':
                        contourIndex === shape.polygon.editor.state.get('drawingPath')
                        && shape.polygon.editor.state.get('drawing')
                    }]"
                  >
                    <template #control>
                      Точек: {{ contour.getLength() }}
                    </template>
                    <template #append>
                      <q-btn
                        v-if="shape.contours.length > 1"
                        label="X"
                        @click.stop="removeContour(shape.polygon, contourIndex)"
                      />
                      <q-btn
                        v-else
                        icon="close"
                        @click.stop="clearContour(contour)"
                      />
                    </template>
                  </q-field>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="area-selection__content-right">
          <div ref="map" class="area-selection__map full-height full-width" />
        </div>
      </q-card-section>
      <q-card-section class="area-selection__footer">
        <slot name="footer" />
      </q-card-section>
    </div>
  </div>
</template>

<script>
/**
 * Компонент редактирования области
 * @module Shared/Component/YMapArea
 * @requires npm:ymaps
 * @requires Utils/validate
 * @fires module:Shared/Component/YMapArea#change
 * @vue-prop {module:PageArea.Area} [area] - Создаваемая/редактируемая зона
 * @vue-prop {Array.<module:PageArea.Area>} [otherAreas] - Другие зоны которые требуется отобразить
 * @vue-prop {Number} [minShapes=1] - Минимально допустимое количество фигур
 * @vue-prop {Number} [maxShapes=null] - Максимально допустимое количество фигур
 * @vue-prop {Number} [minContours=1] - Минимальное допустимое количество контуров
 * @vue-prop {Number} [maxContours=]1 - Максимально допустимое количество контуров
 * @vue-prop {Array.<Number>} [center] - Точка по которой будет отцентрированна карта
 * @vue-prop {Object} [placemark] - Метка на карте
 * @vue-data {Object} ymaps - Яндекс карты
 * @vue-data {Object} myMap - Экземпляр Яндекс карты
 * @vue-data {Array.<Object>} shapes - Основные фигуры
 *   относящиеся к редактируеммой в данный момент зоне
 * @vue-data {Array.<Object>} otherPolygons - Дополнительные фигуры, только отображение
 * @vue-event {Object} change - Зона была изменена
 * */

import ymaps from 'ymaps'
import { requiredRule, numberRangeRuleFabric } from 'Utils/validate'

export default {
  props: {
    /**
     * Создаваемая/редактируемая зона
     * @type {module:PageArea.Area}
     * */
    area: {
      type: Object,
      default: () => ({
        name: '',
        description: '',
        shapes: []
      })
    },
    /**
     * Другие зоны которые требуется отобразить
     * @type {Array.<module:PageArea.Area>}
     * */
    otherAreas: {
      type: Array,
      default: () => ([])
    },
    /**
     * Минимально допустимое количество фигур
     * @type {Number}
     * */
    minShapes: {
      type: Number,
      default: 1
    },
    /**
     * Максимально допустимое количество фигур
     * @type {Number}
     * */
    maxShapes: {
      type: Number,
      default: null
    },
    /**
     * Минимальное допустимое количество контуров
     * @type {Number}
     * */
    minContours: {
      type: Number,
      default: 1
    },
    /**
     * Максимально допустимое количество контуров
     * @type {Number}
     * */
    maxContours: {
      type: Number,
      default: 1
    },
    /**
     * Точка по которой будет отцентрированна карта
     * @type {Array.<Number>}
     * */
    center: Array,
    /**
     * Метка на карте
     * @type {Object}
     * */
    placemark: Object
  },
  data() {
    return {
      /**
       * Центр карты относительно полигонов
       * @type {Object}
       * */
      polygonsCenter: {},
      /**
       * Яндекс карты
       * @type {Object}
       * */
      ymaps: null,
      /**
       * Экземпляр Яндекс карты
       * @type {Object}
       * */
      myMap: null,
      /**
       * Основные фигуры относящиеся к редактируеммой в данный момент зоне
       * @type {Array.<Object>}
       * */
      shapes: [],
      /**
       * Дополнительные фигуры, только отображение
       * @type {Array.<Object>}
       * */
      otherPolygons: []
    }
  },
  watch: {
    /**
     * Наблюдатель за редактируемой зоной, запускает процес инициализации при изменении
     * @type {Function}
     * @name watch/area
     * */
    area: {
      handler() {
        this.init()
      },
      immediate: true
    },
    /**
     * Наблюдатель за внешним свойстом "center", запускает процес инициализации при изменении
     * @type {Function}
     * @name watch/center
     * */
    center: {
      handler() {
        this.init()
      },
      immediate: true
    }
  },
  methods: {
    requiredRule,
    numberRangeRule: numberRangeRuleFabric(3),

    /**
     * Инициализация/реинициализация компонента
     * @async
     * */
    async init() {
      if (!this.ymap) {
        this.ymap = await ymaps.load(YMAPS)
      }

      if (this.myMap) {
        this.myMap.destroy()
      }

      try {
        const geometries = this.area.shapes.map(shape => shape.polygon.geometry[0])
        const merged = geometries.reduce((sum, current) => sum.concat(current), [])
        const aries = []

        for (let i = 0; i < merged.length; i++) {
          aries.push({
            x: merged[i][0],
            y: merged[i][1]
          })
        }

        this.polygonsCenter = this.getPolygonCentroid(aries)
      } catch (e) {
        this.polygonsCenter = {
          x: 55.76,
          y: 37.64
        }
      }
      this.$set(this, 'myMap', new this.ymap.Map(this.$refs.map, {
        center: [this.polygonsCenter.x, this.polygonsCenter.y],
        zoom: 10,
        controls: ['zoomControl', 'geolocationControl', 'typeSelector']
      }))

      if (this.placemark?.coordinates?.length) {
        this.myMap.geoObjects.add(new this.ymap.Placemark(this.placemark.coordinates, {
          balloonContent: this.placemark.balloonContent,
          hintContent: this.placemark.hintContent
        }, {
          preset: 'islands#governmentCircleIcon',
          iconColor: '#3b5998'
        }))
      }

      this.initOtherShapes()

      this.initMainShapes()

      this.startDrawing(this.shapes[0].polygon)
    },

    /**
     * Инициализация/реинициализация дополнительных фигур на карте
     * */
    initOtherShapes() {
      this.otherPolygons = []
      this.otherAreas.forEach(area => {
        if (area.shapes?.length) {
          area.shapes.forEach(shape => {
            this.createOtherShape(shape, {
              balloonContent: area.description,
              hintContent: area.name
            })
          })
        }
      })
    },

    /**
     * Инициализация/реинициализация основых фигур на карте
     * */
    initMainShapes() {
      this.shapes = []
      if (this.area.shapes?.length) {
        this.area.shapes.forEach(shape => this.createMainShape(shape, {
          hintContent: this.area.name,
          balloonContent: this.area.description
        }))
      } else {
        this.createMainShape(null, {
          hintContent: this.area.name,
          balloonContent: this.area.description
        })
      }
    },
    /**
     * Инициализация цетра полигонов на карте
     * */
    getPolygonCentroid(pts) {
      const first = pts[0]
      const last = pts[pts.length - 1]
      if (first.x !== last.x || first.y !== last.y) pts.push(first)
      let twicearea = 0
      let x = 0
      let y = 0
      const nPts = pts.length
      let p1
      let p2
      let f
      for (let i = 0, j = nPts - 1; i < nPts; j = i++) {
        p1 = pts[i]; p2 = pts[j]
        f = p1.x * p2.y - p2.x * p1.y
        twicearea += f
        x += (p1.x + p2.x) * f
        y += (p1.y + p2.y) * f
      }
      f = twicearea * 3
      return {
        x: x / f,
        y: y / f
      }
    },
    /**
     * Оповещение о изменении зоны
     * @fires module:Shared/Component/YMapArea#change
     * */
    emitChange() {
      /**
       * Зона была изменена
       * @event module:Shared/Component/YMapArea#change
       * @type {Object}
       * */
      this.$emit('change', {
        ...this.area,
        shapes: this.shapes.map(shape => ({
          id: shape.id,
          polygon: {
            geometry: shape.polygon.geometry.getCoordinates()
          }
        }))
      })
    },

    /**
     * Добавить контур в переданный полигон
     * @param {Object} polygon
     * */
    addContour(polygon) {
      polygon.geometry.insert(0, [])
    },

    /**
     * Установить контур как редактируемый
     * @param {Object} polygon - полигон в котором находится контур
     * @param {Number} index - индекс контура в переданном полигоне
     * */
    selectContour(polygon, index = 0) {
      polygon.editor.state.set('drawingPath', index)
    },

    /**
     * Удалить контур из полигона
     * @param {Object} polygon - полигон из которого нужно удалить контур
     * @param {Number} index - индекс контура в полигоне
     * */
    removeContour(polygon, index) {
      polygon.geometry.remove(index)
    },

    /**
     * Очистить контур
     * @param {Object} contour
     * */
    clearContour(contour) {
      contour.setCoordinates([])
    },

    /**
     * Создать фигуру относящуюся к редактируемой зоне
     * Вешает обработчик на изменение геометрии фигуры
     * @listens ymaps.Polygon#geometrychange
     * @param {Object} shape - фигура с геометрией
     * @param {Object} options - опции создаваемого полигона (balloon, hint, etc)
     * @returns {Object} newLocalShape
     * */
    createMainShape(shape = {}, options) {
      const contours = []
      const polygon = this.createPolygon(shape?.polygon?.geometry ?? [], options)
      if (!polygon.geometry.getLength()) this.addContour(polygon)

      const onGeometryChange = () => {
        /*
        * После перетаскивания фигуры все контуры отвзяывются и никак не влияют на целевую фигуру
        * Чтоб иметь возможность взаимодействовть с контурами
        * приходится каждый раз получать их заново
        * */
        contours.length = 0
        Array(polygon.geometry.getLength()).fill(null)
          .forEach((el, index) => {
            contours.push(polygon.geometry.getChildGeometry(index))
          })

        this.emitChange()
      }

      const newLocalShape = {
        ...shape,
        contours,
        polygon
      }
      this.shapes.unshift(newLocalShape)

      onGeometryChange()
      polygon.events.add('geometrychange', onGeometryChange)
      return newLocalShape
    },

    /**
     * Добавить новую фигуру к редактируемой зоне
     * */
    addMainShape() {
      const shape = this.createMainShape()
      this.startDrawing(shape.polygon)
    },

    /**
     * Создать дополнительную фигуру, не относящуюся к редактируеммо зоне
     * @param {Object} shape - фигура с геометрией
     * @param {Object} shape.polygon - полигон
     * @param {Object} options - опции создаваемого полигона (balloon, hint, etc)
     * */
    createOtherShape({ polygon }, options) {
      this.otherPolygons.push(this.createPolygon(polygon.geometry, options))
    },

    /**
     * Создать полигон и добавить его на карту
     * @param {Array.<Object>} contours - геометрия полигона
     * @param {Object} options - опции создаваемого полигона (balloon, hint, etc)
     * @returns {Object} polygon
     * */
    createPolygon(contours = [], options = {}) {
      const self = this

      const polygon = new this.ymap.Polygon(contours, options, {
        // Курсор в режиме добавления новых вершин.
        editorDrawingCursor: 'crosshair',
        draggable: true
        /*
          // Максимально допустимое количество вершин.
          editorMaxPoints: undefined,
          // Цвет заливки.
          fillColor: '#00FF00',
          // Цвет обводки.
          strokeColor: '#0000FF',
          // Ширина обводки.
          strokeWidth: 5
          */
      })
      polygon.editor.options.set({
        // Можно поставить точку внутри фигуры
        drawOver: false,
        minPoints: 3,
        // Автоматическое смещения центра карты при приближении курсора к краю карты
        // в момент добавления новой вершины.
        useAutoPanInDrawing: false,
        menuManager(menuOptions) {
          // Завершить возможность редактирования через контекстное меню после окончанчия рисования полигона
          let exit = menuOptions.find(el => el.id == 'stopDrawing')
          exit.onClick = function () {
            polygon.editor.stopDrawing();
            polygon.editor.stopEditing();
          }
          // Убрать возможность добавления внутреннего контура через контекстное меню вершины
          if (polygon.geometry.getLength() >= self.maxContours) {
            return menuOptions.filter(el => el.id !== 'addInterior')
          }
          return menuOptions.map(el => {
            if (el.id === 'addInterior') {
              return {
                ...el,
                title: 'Добавить контур'
              }
            }
            return el
          })
        }
      })
      this.myMap.geoObjects.add(polygon)
      return polygon
    },

    /**
     * Удалить фигуру
     * @param {Object} shape
     * */
    removeShape(shape) {
      const shapeIndex = this.shapes.findIndex(el => el === shape)
      this.myMap.geoObjects.remove(shape.polygon)
      this.shapes.splice(shapeIndex, 1)
      this.emitChange()
    },

    /**
     * Начать редактироваие полигона
     * @param {Object} polygon
     * */
    startDrawing(polygon) {
      this.shapes.forEach(shape => {
        shape.polygon.editor.stopDrawing()
        shape.polygon.editor.stopEditing()
      })
      // Включаем режим редактирования с возможностью добавления новых вершин.
      polygon.editor.startDrawing()
    },

    /**
     * Завершить редактироваие полигона
     * @param {Object} polygon
     * */
    stopDrawing(polygon) {
      // Включаем режим редактирования с возможностью добавления новых вершин.
      polygon.editor.stopDrawing()
      polygon.editor.stopEditing()
    }
  }
}
</script>

<style lang="scss">
.area-selection {
  display: flex;
  flex-direction: column;
}

.area-selection__form {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  border-radius: 0;
  background-color: transparent;
  padding: 0;
  margin: 0;
  box-shadow: none;
}

.area-selection__header,
.area-selection__footer {
  &:empty {
    display: none;
  }
}

.area-selection__content {
  display: flex;
  flex-grow: 1;
}

.area-selection__content-left {
  display: flex;
  flex-direction: column;
  min-width: 270px;
}

.area-selection__content-right {
  flex-grow: 1;
  margin-left: 20px;
}

.area-selection__map {
  flex-grow: 1;
  min-height: 500px;
  overflow: hidden;
}

.figures-list {
  position: relative;
  flex-grow: 1;

  .polygon {
    padding: 5px;
    border: 1px solid gray;
    margin-right: 5px;

    &:not(:last-child) {
      margin-bottom: 15px;
    }

    &__controls {
      display: flex;
      flex-direction: row-reverse;
      padding-right: 16px;

      button {
        margin-left: 5px;
      }
    }
  }
}

.figures-list__scroll-area {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: auto;
}
</style>
